import { useEffect, useMemo, useState } from "react";
import { db, functions } from "./firebase";
import {
  collection, doc, onSnapshot, query, where, writeBatch, deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { isEarlyBird, priceForGrade, formatPence, remainingForGrade } from "./lib/pricing";
import { computePlayerAverages } from "./lib/playerAverages";
import { useResults } from "./lib/ResultsContext";

// Seeds scale with however many ranked players have currently entered a
// grade — 1 seed per 4 entrants (so 32 entrants -> 8 seeds), same convention
// as a standard draw. Recomputes live as more people register.
function seedCount(rankedCount) {
  return rankedCount > 0 ? Math.max(1, Math.round(rankedCount / 4)) : 0;
}

var GRADE_COLOUR = { "Grade A": "#FFB020", "Grade B": "#7BA7FF", "Grade C": "#6DC878" };

var card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.15)", borderRadius: 14, padding: "20px 22px" };
var inputStyle = { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "11px 14px", fontSize: 14, outline: "none", fontFamily: "sans-serif" };
var btnStyle = { padding: "12px 20px", background: "linear-gradient(135deg,#FFB020,#FF6B00)", border: "none", borderRadius: 8, color: "#000", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" };
var btnSecondaryStyle = { padding: "10px 16px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#ccc", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "sans-serif" };

function gradeSlug(grade) { return (grade || "").replace(/\s+/g, ""); }
function entryIdFor(tournamentId, uid, grade) { return tournamentId + "_" + uid + "_" + gradeSlug(grade); }

function CapacityLabel(props) {
  var tournament = props.tournament;
  var grade = props.grade;
  var enteredCount = props.enteredCount;
  var remaining = remainingForGrade(tournament, grade, enteredCount);
  if (remaining === null) return null;
  if (remaining === 0) return <span style={{ color: "#FF6B6B" }}>{" · FULL"}</span>;
  return <span>{" · " + remaining + " space" + (remaining === 1 ? "" : "s") + " remaining"}</span>;
}

function EntrantsList(props) {
  var tournament = props.tournament;
  var entries = props.entries;
  var averages = props.averages;
  var rankByPlayer = props.rankByPlayer;
  var onNavigateToPlayer = props.onNavigateToPlayer;
  var canon = useResults().canon;

  if (entries === null) return <p style={{ fontSize: 13, color: "#666" }}>Loading entrants...</p>;
  var paid = entries.filter(function(e) { return e.paymentStatus === "paid"; });

  var byGrade = {};
  paid.forEach(function(e) {
    if (!byGrade[e.grade]) byGrade[e.grade] = [];
    byGrade[e.grade].push(e);
  });

  var gradesWithCapacityInfo = tournament.grades.filter(function(g) {
    return byGrade[g] || (tournament.maxEntries && tournament.maxEntries[g]);
  });
  if (gradesWithCapacityInfo.length === 0) {
    return <p style={{ fontSize: 13, color: "#666" }}>No one's entered yet — be the first!</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {gradesWithCapacityInfo.map(function(g) {
        var gc = GRADE_COLOUR[g] || "#aaa";
        var gradeEntries = byGrade[g] || [];

        var ranked = gradeEntries
          .filter(function(e) { return rankByPlayer[canon(e.playerName)] != null; })
          .sort(function(a, b) { return rankByPlayer[canon(a.playerName)] - rankByPlayer[canon(b.playerName)]; });
        var unranked = gradeEntries
          .filter(function(e) { return rankByPlayer[canon(e.playerName)] == null; })
          .sort(function(a, b) { return a.playerName.localeCompare(b.playerName); });
        var numSeeds = seedCount(ranked.length);
        var seedByEntryId = {};
        ranked.slice(0, numSeeds).forEach(function(e, i) { seedByEntryId[e.id] = i + 1; });
        var orderedEntries = ranked.concat(unranked);

        return (
          <div key={g}>
            <div style={{ fontSize: 12, color: gc, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
              {g + " · " + gradeEntries.length + " entered"}
              <CapacityLabel tournament={tournament} grade={g} enteredCount={gradeEntries.length} />
            </div>
            {orderedEntries.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {orderedEntries.map(function(e) {
                  var avg = averages[canon(e.playerName)];
                  var seed = seedByEntryId[e.id];
                  return (
                    <button
                      key={e.id}
                      onClick={function() { onNavigateToPlayer(e.playerName); }}
                      style={{ fontSize: 13, color: "#FFB020", textDecoration: "underline", background: "rgba(255,255,255,0.04)", border: "1px solid " + (seed ? "rgba(255,176,32,0.5)" : "rgba(255,255,255,0.08)"), borderRadius: 20, padding: "5px 14px", cursor: "pointer", fontFamily: "sans-serif" }}
                    >
                      {seed && <span style={{ color: "#000", background: "#FFB020", textDecoration: "none", fontWeight: 800, borderRadius: 10, padding: "1px 6px", marginRight: 6, fontSize: 11 }}>{"SEED " + seed}</span>}
                      {e.playerName}
                      {avg && <span style={{ color: "#7BA7FF", textDecoration: "none" }}>{" · avg " + avg.average}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function paidCountsByGrade(entries) {
  var counts = {};
  (entries || []).forEach(function(e) {
    if (e.paymentStatus !== "paid") return;
    counts[e.grade] = (counts[e.grade] || 0) + 1;
  });
  return counts;
}

function RegisterForm(props) {
  var tournament = props.tournament;
  var user = props.user;
  var onRegistered = props.onRegistered;
  var availableGrades = props.availableGrades;
  var counts = paidCountsByGrade(props.entries);
  var isFull = function(g) { return remainingForGrade(tournament, g, counts[g] || 0) === 0; };
  var firstOpenGrade = availableGrades.filter(function(g) { return !isFull(g); })[0];
  var [grade, setGrade] = useState(firstOpenGrade || availableGrades[0] || "");
  var [phone, setPhone] = useState("");
  var [error, setError] = useState("");
  var [submitting, setSubmitting] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!grade) { setError("Please select a grade."); return; }
    if (isFull(grade)) { setError(grade + " is full — please choose another grade."); return; }
    if (!phone.trim()) { setError("Please enter your mobile number — it's used to add you to the WhatsApp group."); return; }
    setSubmitting(true);
    var entryId = entryIdFor(tournament.id, user.uid, grade);
    var entryRef = doc(db, "tournamentEntries", entryId);
    var privateRef = doc(db, "tournamentEntryPrivate", entryId);
    var batch = writeBatch(db);
    batch.set(entryRef, {
      uid: user.uid,
      tournamentId: tournament.id,
      playerName: user.playerName || user.email,
      grade: grade,
      paymentStatus: "pending",
      createdAt: new Date().toISOString(),
    });
    batch.set(privateRef, {
      uid: user.uid,
      tournamentId: tournament.id,
      phone: phone.trim(),
      whatsappConfirmed: false,
      createdAt: new Date().toISOString(),
    });
    batch.commit()
      .then(function() { return onRegistered(entryId); })
      .catch(function(err) { setError(err.message || "Something went wrong. Please try again."); setSubmitting(false); });
  }

  var early = isEarlyBird(tournament);

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
      {error && <div style={{ background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#ff8080", marginBottom: 14 }}>{error}</div>}
      <div style={{ fontSize: 11, color: "#888", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Grade</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {availableGrades.map(function(g) {
          var gc = GRADE_COLOUR[g] || "#aaa";
          var active = grade === g;
          var price = priceForGrade(tournament, g);
          var full = isFull(g);
          var remaining = remainingForGrade(tournament, g, counts[g] || 0);
          return (
            <button type="button" key={g} disabled={full} onClick={function() { setGrade(g); }} style={{ background: active ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.04)", border: "1px solid " + (active ? gc : "rgba(255,255,255,0.1)"), borderRadius: 8, color: full ? "#555" : (active ? gc : "#888"), padding: "10px 16px", fontSize: 13, cursor: full ? "not-allowed" : "pointer", fontWeight: active ? 700 : 400, opacity: full ? 0.6 : 1 }}>
              {g}
              <div style={{ fontSize: 15, fontWeight: 800, color: full ? "#555" : (active ? "#fff" : "#aaa"), marginTop: 2 }}>{full ? "FULL" : formatPence(price)}</div>
              {!full && remaining !== null && <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{remaining + " left"}</div>}
            </button>
          );
        })}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Mobile Number</div>
        <input style={inputStyle} type="tel" value={phone} onChange={function(e) { setPhone(e.target.value); }} placeholder="07700 900123" />
        <div style={{ fontSize: 11, color: "#555", marginTop: 6 }}>Used by admin to check you're in the tournament WhatsApp group — never shown to other players.</div>
      </div>
      <button type="submit" style={btnStyle} disabled={submitting || isFull(grade)}>
        {submitting ? "Setting up payment..." : isFull(grade) ? "This grade is full" : "Register & Pay " + formatPence(priceForGrade(tournament, grade)) + (early ? " (early bird)" : "")}
      </button>
    </form>
  );
}

function MyEntryStatus(props) {
  var tournament = props.tournament;
  var entry = props.entry;
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState("");

  function payNow() {
    setBusy(true); setError("");
    var createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
    createCheckoutSession({ entryId: entry.id })
      .then(function(res) { window.location.href = res.data.url; })
      .catch(function(err) { setError(err.message || "Couldn't start payment. Please try again."); setBusy(false); });
  }

  function cancelEntry() {
    if (!window.confirm("Cancel your entry into " + tournament.name + "?")) return;
    setBusy(true);
    Promise.all([
      deleteDoc(doc(db, "tournamentEntries", entry.id)),
      deleteDoc(doc(db, "tournamentEntryPrivate", entry.id)),
    ]).catch(function(err) { setError(err.message); setBusy(false); });
  }

  if (entry.paymentStatus === "paid") {
    return (
      <div style={{ marginTop: 16, background: "rgba(109,200,120,0.08)", border: "1px solid rgba(109,200,120,0.3)", borderRadius: 10, padding: "14px 18px" }}>
        <div style={{ fontSize: 14, color: "#6DC878", fontWeight: 700 }}>{"✓ You're entered — " + entry.grade}</div>
        {entry.amountPaid != null && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{"Paid " + formatPence(entry.amountPaid)}</div>}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16, background: "rgba(255,176,32,0.06)", border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "14px 18px" }}>
      {error && <div style={{ color: "#ff8080", fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ fontSize: 14, color: "#FFB020", fontWeight: 700, marginBottom: 10 }}>{"Registered for " + entry.grade + " — payment not completed yet"}</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={payNow} style={btnStyle} disabled={busy}>{busy ? "Please wait..." : "Complete Payment"}</button>
        <button onClick={cancelEntry} style={{ ...btnSecondaryStyle, color: "#ff8080", borderColor: "rgba(255,80,80,0.3)" }} disabled={busy}>Cancel Entry</button>
      </div>
    </div>
  );
}

function TournamentCard(props) {
  var tournament = props.tournament;
  var user = props.user;
  var myEntries = props.myEntries || [];
  var expandedByDefault = props.expandedByDefault;
  var [expanded, setExpanded] = useState(!!expandedByDefault);
  var early = isEarlyBird(tournament);
  var [entries, setEntries] = useState(null);

  useEffect(function() {
    if (!expanded) return;
    var q = query(collection(db, "tournamentEntries"), where("tournamentId", "==", tournament.id));
    var unsub = onSnapshot(q, function(snap) {
      setEntries(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
    });
    return unsub;
  }, [tournament.id, expanded]);

  return (
    <div style={card}>
      <div onClick={function() { setExpanded(!expanded); }} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>{tournament.name}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
            {new Date(tournament.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            {tournament.venue ? " · " + tournament.venue : ""}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {tournament.grades.map(function(g) {
              var gc = GRADE_COLOUR[g] || "#aaa";
              return <span key={g} style={{ fontSize: 10, color: gc, border: "1px solid " + gc + "44", borderRadius: 20, padding: "2px 8px" }}>{g}</span>;
            })}
            {early && <span style={{ fontSize: 10, color: "#6DC878", border: "1px solid rgba(109,200,120,0.4)", borderRadius: 20, padding: "2px 8px" }}>Early bird price ends {new Date(tournament.earlyBirdCutoff).toLocaleDateString("en-GB")}</span>}
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#555" }}>{expanded ? "▲" : "▼"}</div>
      </div>

      {expanded && (
        <div>
          {myEntries.map(function(entry) {
            return <MyEntryStatus key={entry.id} tournament={tournament} entry={entry} user={user} />;
          })}
          {(function() {
            var enteredGrades = myEntries.map(function(e) { return e.grade; });
            var availableGrades = tournament.grades.filter(function(g) { return enteredGrades.indexOf(g) === -1; });
            if (availableGrades.length === 0) return null;
            return <RegisterForm tournament={tournament} user={user} entries={entries} availableGrades={availableGrades} onRegistered={props.onRegistered} />;
          })()}
          <div style={{ marginTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
            <div style={{ fontSize: 12, color: "#FFB020", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>Who's Entered</div>
            <EntrantsList tournament={tournament} entries={entries} averages={props.averages} rankByPlayer={props.rankByPlayer} onNavigateToPlayer={props.onNavigateToPlayer} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function TournamentEntryPage(props) {
  var user = props.user;
  var focusEntryId = props.focusEntryId;
  var paymentReturn = props.paymentReturn;
  var [tournaments, setTournaments] = useState(null);
  var [myEntries, setMyEntries] = useState({});
  var results = useResults();
  var averages = useMemo(function() { return computePlayerAverages(results.matches); }, [results.matches]);
  var rankByPlayer = useMemo(function() {
    var map = {};
    results.rankings.forEach(function(r, i) { map[r.player] = i + 1; });
    return map;
  }, [results.rankings]);

  useEffect(function() {
    var q = query(collection(db, "tournaments"), where("status", "==", "open"));
    var unsub = onSnapshot(q, function(snap) {
      var list = snap.docs.map(function(d) { return { id: d.id, ...d.data() }; });
      list.sort(function(a, b) { return a.date.localeCompare(b.date); });
      setTournaments(list);
    });
    return unsub;
  }, []);

  useEffect(function() {
    if (!user) return;
    var q = query(collection(db, "tournamentEntries"), where("uid", "==", user.uid));
    var unsub = onSnapshot(q, function(snap) {
      var map = {};
      snap.docs.forEach(function(d) {
        var entry = { id: d.id, ...d.data() };
        if (!map[entry.tournamentId]) map[entry.tournamentId] = [];
        map[entry.tournamentId].push(entry);
      });
      setMyEntries(map);
    });
    return unsub;
  }, [user]);

  function goPayNow(entryId) {
    var createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
    return createCheckoutSession({ entryId: entryId })
      .then(function(res) { window.location.href = res.data.url; });
  }

  if (!user) {
    return (
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "60px 20px", textAlign: "center", color: "#888", fontFamily: "sans-serif" }}>
        {paymentReturn === "success" ? (
          <>
            <div style={{ fontSize: 15, color: "#6DC878", marginBottom: 8 }}>Payment went through — thanks!</div>
            <div>You've been logged out in the process. Please log back in and your entry will be here waiting for you.</div>
          </>
        ) : (
          "Please log in to register for a tournament."
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 20px", fontFamily: "sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 36 }}>{"🎟"}</div>
        <h1 style={{ margin: "8px 0 4px", fontSize: 36, fontWeight: 800, textTransform: "uppercase", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Enter a Tournament</h1>
        <p style={{ color: "#666", fontSize: 13, margin: 0 }}>Register and pay online to secure your spot</p>
      </div>

      {paymentReturn === "success" && <div style={{ background: "rgba(109,200,120,0.1)", border: "1px solid rgba(109,200,120,0.3)", borderRadius: 10, padding: "12px 18px", fontSize: 13, color: "#6DC878", marginBottom: 20, textAlign: "center" }}>Payment received — thanks! It can take a few seconds to confirm below.</div>}
      {paymentReturn === "cancelled" && <div style={{ background: "rgba(255,176,32,0.08)", border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "12px 18px", fontSize: 13, color: "#FFB020", marginBottom: 20, textAlign: "center" }}>Payment was cancelled — your registration is saved, you can complete payment below whenever you're ready.</div>}

      {tournaments === null ? (
        <p style={{ textAlign: "center", color: "#555" }}>Loading tournaments...</p>
      ) : tournaments.length === 0 ? (
        <p style={{ textAlign: "center", color: "#555" }}>No tournaments are open for entry right now — check back soon.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {tournaments.map(function(t) {
            var tEntries = myEntries[t.id] || [];
            var hasFocusedEntry = tEntries.some(function(e) { return e.id === focusEntryId; });
            return (
              <TournamentCard
                key={t.id}
                tournament={t}
                user={user}
                myEntries={tEntries}
                expandedByDefault={hasFocusedEntry || tournaments.length === 1}
                onRegistered={goPayNow}
                averages={averages}
                rankByPlayer={rankByPlayer}
                onNavigateToPlayer={props.onNavigateToPlayer}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
